/**
 * Competitor requests on an analysis run: discovery and its superseding
 * retry, manual entry, confirmation and rejection, replacement, and the
 * freeze of the confirmed set.
 */

import { request } from './request.js';

export function discoverAnalysisCompetitors(
  runId: string,
  idempotencyKey: string,
  offerLocalId: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/competitors/discover`,
    { idempotencyKey, body: { offerLocalId } },
  );
}

/**
 * Reviewer action: supersede a technically failed discovery attempt in the
 * same analysis run, reopening it for a fresh discovery pass.
 */
export function retryAnalysisCompetitorDiscovery(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/competitors/discovery/retry`,
    { idempotencyKey },
  );
}

export function addAnalysisCompetitorManually(
  runId: string,
  idempotencyKey: string,
  body: {
    readonly name: string;
    readonly canonicalUrl: string;
    readonly offer?: string;
    readonly geography?: string;
    readonly deliveryMode?: string;
    readonly audience?: string;
    readonly need?: string;
    readonly positioning?: string;
    readonly evidenceNote?: string;
    readonly commercialOfferObserved?: boolean;
    readonly reason?: string;
  },
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/competitors/manual`,
    { idempotencyKey, body },
  );
}

export function decideAnalysisCompetitor(
  runId: string,
  competitorId: string,
  action: 'confirm' | 'reject',
  idempotencyKey: string,
  reason?: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/competitors/${encodeURIComponent(competitorId)}/${action}`,
    { idempotencyKey, body: reason ? { reason } : {} },
  );
}

export function replaceAnalysisCompetitor(
  runId: string,
  idempotencyKey: string,
  body: {
    readonly priorCompetitorId: string;
    readonly replacementCompetitorId: string;
    readonly reason: string;
  },
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/competitors/replace`,
    { idempotencyKey, body },
  );
}

export function freezeAnalysisCompetitorSet(
  runId: string,
  idempotencyKey: string,
): Promise<unknown> {
  return request(
    'POST',
    `/workspace/analysis/runs/${encodeURIComponent(runId)}/competitors/freeze`,
    { idempotencyKey },
  );
}
